import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/site";

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 z-50 w-full transition-[background-color,border-color] duration-500 ${
          scrolled ? "border-b border-line bg-ink/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 md:h-20 lg:px-8">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
            <img src="/neo-logo.png" alt="NEO CONSTRUCTION 株式会社ネオ建設" className="h-9 w-auto rounded-[6px] md:h-11" />
            <span className="hidden sm:block">
              <span className="block text-sm font-bold tracking-[0.12em] text-white">株式会社ネオ建設</span>
              <span className="block font-mono text-[9px] tracking-[0.25em] text-accent">NEO CONSTRUCTION</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-4 md:flex lg:gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link${l.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-xs font-semibold tracking-[0.15em] transition-colors lg:text-sm lg:tracking-[0.2em] ${isActive ? "text-accent" : "text-white/70 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="header-contact-cta"
              className="group flex items-center gap-2 bg-accent px-4 py-2 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover lg:px-5 lg:py-2.5 lg:text-sm"
            >
              CONTACT
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </nav>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="p-2 text-white md:hidden"
            aria-label="メニュー"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-lg md:hidden"
          >
            <div className="flex h-full flex-col justify-center px-8 pt-16">
              {[...NAV_LINKS, { to: "/contact", label: "CONTACT", ja: "お問い合わせ" }].map((l, i) => (
                <motion.div key={l.to} initial={{ x: -32, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.06 * i }}>
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link${l.to.replace(/\//g, "-")}`}
                    className="group flex items-baseline gap-4 border-b border-line py-4"
                  >
                    <span className="font-display text-3xl font-bold tracking-[0.15em] text-white transition-colors group-hover:text-accent">
                      {l.label}
                    </span>
                    <span className="text-xs text-gray-500">{l.ja}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
